/* eslint-disable import/consistent-type-specifier-style, import/no-duplicates, react-hooks/exhaustive-deps, react-hooks/set-state-in-effect, ts/prefer-nullish-coalescing */
import type { ColumnDef } from '@tanstack/react-table'
import { useNavigate, useParams } from '@tanstack/react-router'
import { generatePath } from '@/shared/libs/generate-path'
import { ArrowLeft } from 'lucide-react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  buildSurveyVersionDraft,
  hasSurveyVersionDraftContent,
  useCreateSurveyVersionMutation,
  useDeleteSurveyQuestionMutation,
  useSurveyQuestionsQuery,
  useSurveyVersionByIdQuery,
  useUpdateSurveyVersionMutation,
  type PersistedSurveyVersionDraft,
  type SurveyQuestionDTO,
} from '@/entities/survey'
import { useTenantByIdQuery, useTenantLanguages } from '@/entities/tenant'
import type { ISurveyTranslationRow } from '@/features/survey/survey-translation-section'
import { SurveyTranslationSection } from '@/features/survey/survey-translation-section'

import { TRANSLATION_JOB_SCOPE } from '@/shared/constants/status'
import {
  localizedDataToValuesByLanguage,
  mergeSurveyTranslationRows,
  surveyEditorLanguageCodes,
  surveyLanguagesFromTenantLanguages,
  surveyTranslationsToLocalizedData,
} from '@/features/survey/lib/survey-localized-mapper'
import {
  filterActiveSurveyQuestions,
  isSurveyQuestionDeleted,
} from '@/features/survey/lib/survey-question-helpers'
import { DEFAULT_PAGE_SIZE, ReasonType } from '@/shared/constants'
import { useDashboardDisplayLanguage } from '@/shared/hooks/use-dashboard-display-language'
import { useLanguageFilter } from '@/shared/hooks/use-language-filter'
import { useScopedAdminPath } from '@/shared/hooks/use-scoped-admin-path'
import { showToast } from '@/shared/libs/toast'
import { resolveDashboardLocalizedValue } from '@/shared/utils/dashboard-localized-value'
import { Badge } from '@/shared/ui/badge'
import { Button } from '@/shared/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/shared/ui/card'
import { ConfirmDialog } from '@/shared/ui/confirm-dialog'
import { DataTable, DataTableFilterField, DataTableToolbar } from '@/shared/ui/data-table'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select'
import { FormField } from '@/shared/ui/form-field'
import { Input } from '@/shared/ui/input'
import { PageContainer } from '@/shared/ui/page-container'
import { PageHeader } from '@/shared/ui/page-header'
import { Skeleton } from '@/shared/ui/skeleton'
import { Switch } from '@/shared/ui/switch'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/shared/ui/dropdown-menu'
import { MoreHorizontal, Trash2 } from 'lucide-react'
import { useCreateTranslationDraft } from '@/features/localized-translation'

function normalizeSurveyLanguageCode(value: string): string {
  return value.trim().toLowerCase().replace(/_/g, '-')
}

function resolveSurveyQuestionLocalizedValue(
  localizedData: SurveyQuestionDTO['localizedData'],
  languageCode: string,
): string {
  if (!localizedData) {
    return '-'
  }
  const entries = Object.entries(localizedData).map(([key, value]) => ({
    key: normalizeSurveyLanguageCode(key),
    value,
  }))
  const selected = entries.find(
    (item) => item.key === normalizeSurveyLanguageCode(languageCode),
  )?.value
  const primary =
    selected?.['question']?.trim() || selected?.['name']?.trim() || selected?.['title']?.trim()
  if (primary) {
    return primary
  }
  for (const entry of entries) {
    const fallback =
      entry.value?.['question']?.trim() ||
      entry.value?.['name']?.trim() ||
      entry.value?.['title']?.trim()
    if (fallback) {
      return fallback
    }
  }
  return '-'
}

function SurveyQuestionActionsCell({
  question,
  versionId,
}: {
  question: SurveyQuestionDTO
  versionId: string
}) {
  const { t } = useTranslation()
  const deleteQuestionMutation = useDeleteSurveyQuestionMutation()
  const [confirmOpen, setConfirmOpen] = useState(false)
  const isDeleted = isSurveyQuestionDeleted(question)

  if (isDeleted) return null

  async function handleDeleteQuestion() {
    if (deleteQuestionMutation.isPending) return
    try {
      await deleteQuestionMutation.mutateAsync({ questionId: question.id, versionId })
      showToast.success(t('survey.deleteQuestionSuccess', { defaultValue: 'Question deleted.' }))
      setConfirmOpen(false)
    } catch {
      showToast.error(
        t('survey.deleteQuestionError', { defaultValue: 'Could not delete question.' }),
      )
    }
  }

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={(e) => e.stopPropagation()}
          >
            <MoreHorizontal className="h-4 w-4" />
            <span className="sr-only">Open menu</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem
            className="text-red-600 focus:text-red-600"
            disabled={deleteQuestionMutation.isPending}
            onClick={(e) => {
              e.stopPropagation()
              setConfirmOpen(true)
            }}
          >
            <Trash2 className="mr-2 h-4 w-4" />
            {t('common.delete')}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <ConfirmDialog
        open={confirmOpen}
        title={t('survey.deleteQuestionTitle', { defaultValue: 'Delete question?' })}
        description={t('survey.deleteQuestionDescription', {
          defaultValue: 'This action cannot be undone.',
        })}
        confirmLabel={t('common.delete')}
        cancelLabel={t('common.cancel')}
        onConfirm={handleDeleteQuestion}
        onCancel={() => setConfirmOpen(false)}
        variant="destructive"
      />
    </>
  )
}

export function SurveyVersionShellPage() {
  const { t } = useTranslation()
  const { activeLanguage } = useDashboardDisplayLanguage()
  const navigate = useNavigate()
  const scopedAdminPath = useScopedAdminPath()
  const params = useParams({ strict: false })
  const tenantId = (params.tenantId as string) ?? ''
  const versionId = (params.versionId as string) ?? ''
  const isCreateMode = !versionId

  const { data: tenantResponse, isLoading: tenantLoading } = useTenantByIdQuery(tenantId)
  const tenantName = useMemo(() => tenantResponse?.data?.name ?? '', [tenantResponse?.data?.name])

  const versionQuery = useSurveyVersionByIdQuery(versionId, {
    enabled: !isCreateMode && !!versionId,
  })
  const version = useMemo(() => versionQuery.data?.data ?? null, [versionQuery.data?.data])

  const tenantLanguagesQuery = useTenantLanguages(tenantId)
  const tenantLanguages = useMemo(
    () => tenantLanguagesQuery.items ?? [],
    [tenantLanguagesQuery.items],
  )
  const languageCodes = useMemo(
    () => surveyLanguagesFromTenantLanguages(tenantLanguages),
    [tenantLanguages],
  )
  const editorLanguageCodes = useMemo(
    () => surveyEditorLanguageCodes(languageCodes, version?.localizedData),
    [languageCodes, version?.localizedData],
  )

  const [qPage, setQPage] = useState(1)
  const [qPageSize, setQPageSize] = useState(DEFAULT_PAGE_SIZE)
  const [showDeletedQuestions, setShowDeletedQuestions] = useState(false)
  const { selectedLanguageCode, setSelectedLanguageCode, languageOptions, defaultLanguageCode } =
    useLanguageFilter(tenantLanguages)
  const effectiveQuestionLanguageCode = selectedLanguageCode || defaultLanguageCode
  const questionsQuery = useSurveyQuestionsQuery(
    tenantId,
    versionId,
    { current: qPage, pageSize: qPageSize },
    effectiveQuestionLanguageCode ? { languageCode: effectiveQuestionLanguageCode } : undefined,
  )
  const questions = useMemo(() => questionsQuery.data?.data ?? [], [questionsQuery.data?.data])
  const qTotal = useMemo(() => questionsQuery.data?.total ?? 0, [questionsQuery.data?.total])
  const activeQuestionsOnly = useMemo(() => filterActiveSurveyQuestions(questions), [questions])
  const tableQuestions = useMemo(
    () => (showDeletedQuestions ? questions : activeQuestionsOnly),
    [questions, showDeletedQuestions],
  )

  const updateVersionMutation = useUpdateSurveyVersionMutation()
  const createVersionMutation = useCreateSurveyVersionMutation()

  const [business, setBusiness] = useState('')
  const [isActive, setIsActive] = useState(true)
  const [surveyTranslations, setSurveyTranslations] = useState<ISurveyTranslationRow[]>([])
  const translationEntityId = isCreateMode
    ? `survey-version-create:${tenantId}`
    : `survey-version:${versionId}`

  const versionDraft = useMemo(
    () =>
      buildSurveyVersionDraft({
        business,
        isActive,
        translations: surveyTranslations,
      }),
    [business, isActive, surveyTranslations],
  )
  const hasVersionDraftContent = useMemo(
    () =>
      hasSurveyVersionDraftContent({
        business,
        isActive,
        translations: surveyTranslations,
      }),
    [business, isActive, surveyTranslations],
  )

  useEffect(() => {
    if (isCreateMode || !version?.id) return
    setBusiness('')
    setIsActive(version.isActive)
    const codes = surveyEditorLanguageCodes(languageCodes, version.localizedData)
    if (codes.length === 0) {
      setSurveyTranslations([])
      return
    }
    const values = localizedDataToValuesByLanguage(version.localizedData, languageCodes, {
      primaryField: 'title',
    })
    setSurveyTranslations(
      codes.map((language) => ({
        language,
        name: values[language]?.name ?? '',
        description: values[language]?.description ?? '',
      })),
    )
  }, [isCreateMode, languageCodes, version?.id, version?.isActive, version?.localizedData])

  const hydrateCreateDraft = useCallback(
    (persistedDraft?: PersistedSurveyVersionDraft) => {
      setBusiness('')
      setIsActive(true)
      setSurveyTranslations(
        mergeSurveyTranslationRows(editorLanguageCodes, persistedDraft?.translations),
      )
    },
    [editorLanguageCodes],
  )
  const { clearCurrentDraft, clearCurrentJobs } = useCreateTranslationDraft({
    enabled: isCreateMode,
    scope: TRANSLATION_JOB_SCOPE.SURVEY_VERSION,
    entityId: translationEntityId,
    audioEnabled: ReasonType.TRANSLATE_AUDIO_AVAILABLE,
    initSignature: `create:${tenantId}:${editorLanguageCodes.join('\0')}`,
    draft: versionDraft,
    hasContent: hasVersionDraftContent,
    onHydrate: hydrateCreateDraft,
  })

  const handleSaveVersion = useCallback(async () => {
    try {
      const localizedData = surveyTranslationsToLocalizedData(surveyTranslations, {
        primaryField: 'title',
      })
      if (isCreateMode) {
        const res = await createVersionMutation.mutateAsync({
          business: '',
          isActive: true,
          localizedData: Object.keys(localizedData).length > 0 ? localizedData : null,
        })
        showToast.success(
          t('survey.createVersionSuccess', { defaultValue: 'Survey version created.' }),
        )
        clearCurrentJobs()
        clearCurrentDraft()
        const newId = res.data?.id
        if (newId && tenantId) {
          void navigate({
            href: scopedAdminPath(
              generatePath('/tenant/:tenantId/surveys/:versionId', { tenantId, versionId: newId }),
            ),
          })
        }
        return
      }
      if (!versionId) return
      await updateVersionMutation.mutateAsync({
        versionId,
        payload: {
          business: '',
          isActive,
          localizedData: Object.keys(localizedData).length > 0 ? localizedData : null,
        },
      })
      showToast.success(t('common.saved', { defaultValue: 'Saved.' }))
      clearCurrentJobs()
      await versionQuery.refetch()
    } catch {
      showToast.error(
        isCreateMode
          ? t('survey.createVersionError', { defaultValue: 'Could not create survey version.' })
          : t('survey.saveVersionError', { defaultValue: 'Could not save survey version.' }),
      )
    }
  }, [
    createVersionMutation,
    clearCurrentDraft,
    clearCurrentJobs,
    isActive,
    isCreateMode,
    navigate,
    scopedAdminPath,
    surveyTranslations,
    t,
    tenantId,
    translationEntityId,
    updateVersionMutation,
    versionId,
    versionQuery,
  ])

  const questionColumns = useMemo<ColumnDef<SurveyQuestionDTO>[]>(
    () => [
      {
        id: 'snippet',
        accessorKey: 'orderIndex',
        header: t('survey.columnQuestion'),
        cell: ({ row }) => {
          const deleted = isSurveyQuestionDeleted(row.original)
          return (
            <span
              className={`flex flex-wrap items-center gap-2 font-medium ${deleted ? 'text-zinc-500 line-through' : 'text-zinc-900'}`}
            >
              {resolveSurveyQuestionLocalizedValue(
                row.original.localizedData,
                effectiveQuestionLanguageCode,
              )}
              {deleted ? (
                <Badge variant="secondary" className="font-normal normal-case no-underline">
                  {t('survey.badgeDeleted', { defaultValue: 'Deleted' })}
                </Badge>
              ) : null}
            </span>
          )
        },
      },
      {
        accessorKey: 'type',
        header: t('survey.columnQuestionType'),
        cell: ({ row }) => <span className="text-zinc-600">{row.original.type}</span>,
      },
      {
        accessorKey: 'orderIndex',
        header: t('survey.columnOrder'),
        cell: ({ row }) => <span className="tabular-nums">{row.original.orderIndex}</span>,
      },
      {
        id: 'actions',
        header: t('common.actions'),
        cell: ({ row }) => (
          <SurveyQuestionActionsCell question={row.original} versionId={versionId} />
        ),
      },
    ],

    [effectiveQuestionLanguageCode, t, versionId],
  )

  const pageTitle = isCreateMode
    ? t('survey.createVersionTitle', { defaultValue: 'Create survey version' })
    : version
      ? resolveDashboardLocalizedValue(version.localizedData, activeLanguage)
      : t('survey.versionDetailTitle', { defaultValue: 'Survey version' })

  return (
    <PageContainer>
      <PageHeader
        title={pageTitle}
        breadcrumbs={[
          { label: t('sidebar.tenants'), path: scopedAdminPath('/tenant') },
          ...(tenantId
            ? [
                {
                  label: tenantLoading ? t('common.loading') : tenantName || tenantId,
                  path: scopedAdminPath(generatePath('/tenant/:tenantId', { tenantId })),
                },
              ]
            : []),
          {
            label: t('survey.listTitle'),
            ...(tenantId
              ? { path: scopedAdminPath(generatePath('/tenant/:tenantId/surveys', { tenantId })) }
              : {}),
          },
          {
            label: isCreateMode
              ? t('common.create', { defaultValue: 'Create' })
              : t('survey.versionBreadcrumb'),
          },
        ]}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            {tenantId ? (
              <Button
                variant="outline"
                size="sm"
                type="button"
                onClick={() =>
                  void navigate({
                    href: scopedAdminPath(generatePath('/tenant/:tenantId/surveys', { tenantId })),
                  })
                }
              >
                <ArrowLeft size={16} />
                {t('common.back')}
              </Button>
            ) : null}
          </div>
        }
      />

      {!isCreateMode && versionQuery.isError && (
        <Card className="mt-4 border-amber-200 bg-amber-50/80">
          <CardContent className="pt-4 text-sm text-amber-900">
            {t('survey.versionLoadFallback', {
              defaultValue:
                'Could not load version details from API. You can still manage questions using the URL id.',
            })}
          </CardContent>
        </Card>
      )}

      {!isCreateMode && versionQuery.isLoading && (
        <div className="mt-6 space-y-3">
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-48 w-full" />
        </div>
      )}

      {(isCreateMode || (!versionQuery.isLoading && version)) && (
        <Card className="mt-6 border-zinc-200">
          <CardHeader className="flex flex-row flex-wrap items-start justify-between gap-3">
            <div>
              <CardTitle className="text-base">
                {t('survey.versionSettingsTitle', { defaultValue: 'Version' })}
              </CardTitle>
              {!isCreateMode && version && (
                <CardDescription className="font-mono text-xs mt-1">{version.id}</CardDescription>
              )}
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            <FormField label={t('survey.fieldBusiness', { defaultValue: 'Business' })}>
              <Input value="" disabled readOnly className="max-w-md" />
            </FormField>
            {editorLanguageCodes.length > 0 ? (
              <SurveyTranslationSection
                blockTitle={t('survey.localizedBlockTitle', { defaultValue: 'Localized content' })}
                languages={editorLanguageCodes}
                translations={surveyTranslations}
                onChange={setSurveyTranslations}
                entityId={translationEntityId}
                entityLabel={
                  isCreateMode
                    ? t('survey.createVersionTitle', { defaultValue: 'Create survey version' })
                    : pageTitle
                }
                scope={TRANSLATION_JOB_SCOPE.SURVEY_VERSION}
                {...(typeof window !== 'undefined' ? { entityPath: window.location.pathname } : {})}
              />
            ) : (
              <p className="text-sm text-amber-700">
                {t('survey.noTenantLanguages', {
                  defaultValue: 'Configure tenant languages to edit localized fields.',
                })}
              </p>
            )}
            <Button
              type="button"
              onClick={() => void handleSaveVersion()}
              disabled={createVersionMutation.isPending || updateVersionMutation.isPending}
            >
              {isCreateMode
                ? t('common.create', { defaultValue: 'Create' })
                : t('common.save', { defaultValue: 'Save' })}
            </Button>
          </CardContent>
        </Card>
      )}

      {!isCreateMode && (
        <div className="mt-8">
          <DataTableToolbar>
            <div className="flex flex-wrap items-center justify-between gap-3 w-full">
              <h2 className="text-sm font-semibold text-zinc-800">
                {t('survey.questionsTitle', { defaultValue: 'Questions' })}
              </h2>
              <div className="flex flex-wrap items-center gap-4">
                <DataTableFilterField label={t('common.language')}>
                  <Select
                    value={effectiveQuestionLanguageCode}
                    onValueChange={(value) => {
                      setSelectedLanguageCode(value)
                      setQPage(1)
                    }}
                    disabled={languageOptions.length < 1}
                  >
                    <SelectTrigger className="w-40">
                      <SelectValue placeholder={t('common.language')} />
                    </SelectTrigger>
                    <SelectContent>
                      {languageOptions.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </DataTableFilterField>
                <label className="flex cursor-pointer items-center gap-2 text-sm text-zinc-600">
                  <Switch
                    checked={showDeletedQuestions}
                    onCheckedChange={setShowDeletedQuestions}
                  />
                  <span>{t('survey.showDeletedQuestions', { defaultValue: 'Show deleted' })}</span>
                </label>
                <Button
                  type="button"
                  size="sm"
                  onClick={() =>
                    void navigate({
                      href: scopedAdminPath(
                        generatePath('/tenant/:tenantId/surveys/:versionId/questions/create', {
                          tenantId,
                          versionId,
                        }),
                      ),
                    })
                  }
                >
                  {t('survey.addQuestion', { defaultValue: 'Add question' })}
                </Button>
              </div>
            </div>
          </DataTableToolbar>

          {!showDeletedQuestions && questions.some((q) => isSurveyQuestionDeleted(q)) ? (
            <p className="mt-2 text-xs text-zinc-500">
              {t('survey.questionsDeletedHiddenHint', {
                defaultValue:
                  'Soft-deleted questions are hidden. Turn on the switch to list them (read-only in detail).',
              })}
            </p>
          ) : null}

          {questionsQuery.isError && (
            <Card className="mt-4 border-red-200 bg-red-50/80">
              <CardContent className="pt-4 text-sm text-red-800">
                {t('survey.questionsLoadError', { defaultValue: 'Could not load questions.' })}
              </CardContent>
            </Card>
          )}

          <div className="mt-4">
            <DataTable
              columns={questionColumns}
              data={tableQuestions}
              loading={questionsQuery.isLoading}
              emptyText={t('survey.emptyQuestions', { defaultValue: 'No questions yet.' })}
              pagination={{
                current: qPage,
                pageSize: qPageSize,
                total: qTotal,
                onChange: (p, ps) => {
                  setQPage(p)
                  setQPageSize(ps)
                },
              }}
              onRowClick={(question) =>
                void navigate({
                  href: scopedAdminPath(
                    generatePath('/tenant/:tenantId/surveys/:versionId/questions/:questionId', {
                      tenantId,
                      versionId,
                      questionId: question.id,
                    }),
                  ),
                })
              }
            />
          </div>
        </div>
      )}
    </PageContainer>
  )
}
